// ---------------------------------------------------------------------------
// WFH location tracking + check-in session helpers (simple version).
//
//   * Persist the current check-in state so an app-wide tab-close guard can
//     read it from any page (see useCheckoutOnTabClose).
//   * While the user is checked in (WFH), capture their geolocation at random
//     intervals and POST each sample to the backend, which stores it together
//     with the browser agent + IP in the user_tracking_location table.
//   * On checkout the frontend simply stops the capture loop.
//   * On tab close we auto-checkout via the existing /auth/logout endpoint.
// ---------------------------------------------------------------------------
import api from "../api/axios";
import config from "../config";
import { getAuthToken } from "./tokenStorage";

const CHECKIN_KEY = "pb_checkin_state";
const TRACKING_KEY = "pb_wfh_tracking";

// Random interval bounds between consecutive location captures (WFH).
const MIN_INTERVAL_MS = 60 * 1000; // 1 minute
const MAX_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

// Module-level timer handle for the current capture loop.
let scheduleTimer = null;

// ---- persisted state helpers ---------------------------------------------

const readJSON = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || null;
  } catch (e) {
    return null;
  }
};

export const getCheckinState = () => readJSON(CHECKIN_KEY);

export const setCheckinState = (state) => {
  localStorage.setItem(CHECKIN_KEY, JSON.stringify(state));
};

export const clearCheckinState = () => {
  localStorage.removeItem(CHECKIN_KEY);
};

export const getTrackingState = () => readJSON(TRACKING_KEY);

const setTrackingState = (state) => {
  localStorage.setItem(TRACKING_KEY, JSON.stringify(state));
};

const clearTrackingState = () => {
  localStorage.removeItem(TRACKING_KEY);
};

export const isCheckedIn = () => !!getCheckinState()?.checkedIn;

export const isWfhAutoCheckoutActive = () => !!getTrackingState()?.active;

// ---- geolocation capture --------------------------------------------------

const getCurrentPosition = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

const captureAndSend = async () => {
  const tracking = getTrackingState();
  if (!tracking?.active) return;

  const pos = await getCurrentPosition();
  if (!pos) return; // user denied / unavailable — try again next tick

  try {
    await api.post("api/tracking/location", {
      employeeId: tracking.employeeId,
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
    });
  } catch (err) {
    // Non-fatal: skip this sample, keep the loop running.
    console.error("Failed to record tracking location", err);
  }
};

// Recursively schedules the next capture at a random delay.
const scheduleNext = () => {
  const tracking = getTrackingState();
  if (!tracking?.active) return;

  const delay =
    Math.floor(Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS + 1)) +
    MIN_INTERVAL_MS;

  scheduleTimer = setTimeout(async () => {
    await captureAndSend();
    scheduleNext();
  }, delay);
};

const stopScheduler = () => {
  if (scheduleTimer) {
    clearTimeout(scheduleTimer);
    scheduleTimer = null;
  }
};

// ---- public API -----------------------------------------------------------

// Marks the user as checked in. Call on every successful check-in so the
// tab-close guard knows a session is live regardless of the current page.
export const markCheckedIn = ({ employeeId, punchDate }) => {
  setCheckinState({ checkedIn: true, employeeId, punchDate });
};

// Clears the checked-in flag. Call on every successful (manual) checkout.
export const markCheckedOut = () => {
  clearCheckinState();
};

// Starts the WFH location capture loop (random intervals). No backend session
// is created — each captured sample is stored directly.
export const startWfhTracking = ({ employeeId, workLocation, punchDate }) => {
  setTrackingState({ active: true, employeeId, workLocation, punchDate });

  stopScheduler();
  // Capture one sample immediately, then continue at random intervals.
  captureAndSend();
  scheduleNext();
};

// Resumes the capture loop for an active session after a full page reload
// (the in-memory timer is lost on reload, the persisted state is not).
export const resumeWfhTracking = () => {
  const tracking = getTrackingState();
  if (!tracking?.active || !isCheckedIn()) return;
  stopScheduler();
  captureAndSend();
  scheduleNext();
};

// Stops the WFH location capture loop.
export const stopWfhTracking = () => {
  stopScheduler();
  clearTrackingState();
};

// Fire-and-forget auto-checkout used when the browser tab is closed. Reuses the
// existing /auth/logout endpoint (checkOut: true) via a keepalive fetch so the
// request survives page unload. Also stops the local capture loop.
export const beaconCheckout = () => {
  const checkin = getCheckinState();
  if (!checkin?.checkedIn) return;

  const url = `${config.API_BASE_URL}auth/logout`;
  const payload = JSON.stringify({
    employeeId: checkin.employeeId,
    punchDate: checkin.punchDate,
    checkOut: true,
  });

  try {
    const token = getAuthToken();
    fetch(url, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: payload,
    });
  } catch (e) {
    // nothing else we can do at unload time
  }

  // Clear local state so a reopened tab does not think we're still checked in.
  stopScheduler();
  clearTrackingState();
  clearCheckinState();
};
